import { del, get, post, put } from '@elementary/common-ui/utils/http';
import {
  AvailableWarehouseConnection,
  CreatedRuntimeToken,
  CreatedRuntimeTokenResponse,
  CreateRuntimeTokenRequest,
  Runtime,
  RuntimeListResponse,
  RuntimeResponse,
  RuntimeToken,
  RuntimeTokenListResponse,
  RuntimeTokenResponse,
  RuntimeWarehouseConnectionListResponse,
  UpdateRuntimeRequest,
} from './types';

const MINUTES_PER_DAY = 1440;

const getRuntimesUrl = () =>
  `${import.meta.env.VITE_API_BASE_URL}/account/runtimes`;

const toRuntime = (runtime: RuntimeResponse): Runtime => ({
  id: runtime.id,
  name: runtime.name,
  adminStatus: runtime.status,
  health: runtime.health,
  lastSeenAt: runtime.last_seen_at,
  onlineInstanceCount: runtime.online_instance_count,
  hasActiveToken: runtime.has_active_token,
  connections: runtime.connections.map((connection) => ({
    id: connection.warehouse_connection_id,
    name: connection.warehouse_connection_name,
    warehouseType: connection.warehouse_type,
    health: connection.health,
    lastVerifiedAt: connection.last_verified_at,
    errorCode: connection.last_error_code,
  })),
  createdAt: runtime.created_at,
});

const toRuntimeToken = (token: RuntimeTokenResponse): RuntimeToken => ({
  id: token.id,
  tokenPrefix: token.token_prefix,
  status: token.status,
  createdAt: token.created_at,
  expiresAt: token.expires_at,
  lastUsedAt: token.last_used_at,
});

export const getRuntimes = async (): Promise<Runtime[]> => {
  const response = await get<RuntimeListResponse>(getRuntimesUrl());
  return response.runtimes.map(toRuntime);
};

export const getRuntime = async (runtimeId: string): Promise<Runtime> => {
  const response = await get<RuntimeResponse>(
    `${getRuntimesUrl()}/${runtimeId}`,
  );
  return toRuntime(response);
};

export const createRuntime = async (name: string): Promise<Runtime> => {
  const response = await post<RuntimeResponse>(getRuntimesUrl(), { name });
  return toRuntime(response);
};

export const updateRuntime = async (
  runtimeId: string,
  { name, adminStatus, warehouseConnectionIds }: UpdateRuntimeRequest,
): Promise<Runtime> => {
  const response = await put<RuntimeResponse>(
    `${getRuntimesUrl()}/${runtimeId}`,
    {
      name,
      status: adminStatus,
      warehouse_connection_ids: warehouseConnectionIds,
    },
  );
  return toRuntime(response);
};

export const deleteRuntime = async (runtimeId: string): Promise<void> => {
  await del(`${getRuntimesUrl()}/${runtimeId}`);
};

export const getWarehouseConnections = async (): Promise<
  AvailableWarehouseConnection[]
> => {
  const response = await get<RuntimeWarehouseConnectionListResponse>(
    `${getRuntimesUrl()}/warehouse-connections`,
  );
  return response.warehouse_connections.map((warehouse) => ({
    id: warehouse.id,
    name: warehouse.name,
    warehouseType: warehouse.warehouse_type,
    runtimeId: warehouse.runtime_id,
    runtimeName: warehouse.runtime_name,
  }));
};

export const getRuntimeTokens = async (
  runtimeId: string,
): Promise<RuntimeToken[]> => {
  const response = await get<RuntimeTokenListResponse>(
    `${getRuntimesUrl()}/${runtimeId}/tokens`,
  );
  return response.tokens.map(toRuntimeToken);
};

export const createRuntimeToken = async (
  runtimeId: string,
  { expiresInMinutes }: CreateRuntimeTokenRequest,
): Promise<CreatedRuntimeToken> => {
  const response = await post<CreatedRuntimeTokenResponse>(
    `${getRuntimesUrl()}/${runtimeId}/tokens`,
    {
      expires_in_days: expiresInMinutes
        ? Math.ceil(expiresInMinutes / MINUTES_PER_DAY)
        : null,
    },
  );
  return { ...toRuntimeToken(response), secret: response.token };
};

export const revokeRuntimeToken = async (
  runtimeId: string,
  tokenId: string,
): Promise<RuntimeToken> => {
  const response = await post<RuntimeTokenResponse>(
    `${getRuntimesUrl()}/${runtimeId}/tokens/${tokenId}/revoke`,
  );
  return toRuntimeToken(response);
};
